import { commitPerpendSolution, normalisePerpendGap, solvePerpendGap } from '@bw/core';
import type { WallModel } from '@bw/core';
import type { SolveRequest, SolveResponse } from '../workers/solver.worker.js';

let worker: Worker | null = null;
let nextId = 1;
/** Generous: a 50,000-brick solve is well under a second. */
const SOLVE_TIMEOUT_MS = 30_000;

function ensureWorker(): Worker | null {
  if (worker !== null) return worker;
  try {
    worker = new Worker(new URL('../workers/solver.worker.ts', import.meta.url), { type: 'module' });
    return worker;
  } catch {
    return null; // no worker support: solve on the main thread instead
  }
}

export interface SolveOutcome {
  readonly changed: boolean;
  readonly error: string | null;
  /** Bricks whose X coordinate would move, for the impact preview (C5). */
  readonly moved: number;
  readonly commit: (() => boolean) | null;
}

/**
 * Solve a perpend change without touching the wall, so the panel can state the
 * impact — or the error — before anything is committed (design document C5).
 */
export async function solveJointChange(
  model: WallModel,
  value: number | string,
): Promise<SolveOutcome> {
  let target: number;
  try {
    target = normalisePerpendGap(value);
  } catch (error) {
    return { changed: false, error: (error as Error).message, moved: 0, commit: null };
  }
  if (Math.abs(target - model.perpendGapMm) < 1e-9) {
    return { changed: false, error: null, moved: 0, commit: null };
  }

  const active = ensureWorker();
  if (active === null) {
    try {
      const solution = solvePerpendGap(model, target);
      if (solution === null) return { changed: false, error: null, moved: 0, commit: null };
      return {
        changed: true,
        error: null,
        moved: countMoved(model, solution.x),
        commit: () => commitPerpendSolution(model, solution),
      };
    } catch (error) {
      return { changed: false, error: (error as Error).message, moved: 0, commit: null };
    }
  }

  const id = nextId++;
  const request: SolveRequest = {
    id,
    records: model.toRecords(),
    currentGapMm: model.perpendGapMm,
    targetGapMm: target,
  };

  return new Promise<SolveOutcome>((resolve) => {
    // A worker that fails to load, or throws at module scope, would otherwise
    // leave this promise unsettled forever — and the busy banner with it.
    let settled = false;
    const finish = (outcome: SolveOutcome): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      active.removeEventListener('message', onMessage);
      active.removeEventListener('error', onFailure);
      active.removeEventListener('messageerror', onFailure);
      resolve(outcome);
    };
    const onFailure = (): void =>
      finish({ changed: false, error: 'The background solver stopped responding.', moved: 0, commit: null });
    const timer = setTimeout(onFailure, SOLVE_TIMEOUT_MS);
    const onMessage = (event: MessageEvent<SolveResponse>): void => {
      if (event.data.id !== id) return;
      const data = event.data;
      if (!data.ok) {
        finish({ changed: false, error: data.error, moved: 0, commit: null });
        return;
      }
      if (!data.changed) {
        finish({ changed: false, error: null, moved: 0, commit: null });
        return;
      }
      const solution = { perpendGapMm: data.perpendGapMm, x: data.x };
      finish({
        changed: true,
        error: null,
        moved: countMoved(model, data.x),
        // The commit revalidates clearance against a candidate wall, so a
        // solve that is arithmetically sound but geometrically impossible
        // still leaves the live wall untouched.
        commit: () => commitPerpendSolution(model, solution),
      });
    };
    active.addEventListener('message', onMessage);
    active.addEventListener('error', onFailure);
    active.addEventListener('messageerror', onFailure);
    active.postMessage(request);
  });
}

function countMoved(model: WallModel, x: Float64Array): number {
  let moved = 0;
  model.bricks.forEach((brick, index) => {
    if (x[index] !== brick.x) moved += 1;
  });
  return moved;
}
